import React, { useEffect, useState } from 'react';

const timeAgo = (timestamp) => {
  if (!timestamp) return 'Never';
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

export const SystemMonitoringPage = () => {
  const [systemHealth, setSystemHealth] = useState({ status: 'loading', score: 0 });
  const [functionStatuses, setFunctionStatuses] = useState([]);
  const [queueDepths, setQueueDepths] = useState({});
  const [metrics, setMetrics] = useState({});

  useEffect(() => {
    const loadData = async () => {
      // Mock data – replace with real monitoring calls
      setSystemHealth({ status: 'healthy', score: 98 });
      setFunctionStatuses([
        { name: 'createRide', status: 'active', lastInvocation: Date.now() - 120000 },
        { name: 'acceptRide', status: 'active', lastInvocation: Date.now() - 300000 },
        { name: 'completeRide', status: 'active', lastInvocation: Date.now() - 180000 },
        { name: 'dispatchRide', status: 'degraded', lastInvocation: Date.now() - 5000 },
        { name: 'capturePayment', status: 'active', lastInvocation: Date.now() - 600000 },
      ]);
      setQueueDepths({ dispatch: 12, payment: 3, notification: 0 });
      setMetrics({ ridesLastHour: 42, apiLatency: 245, errorRate: 1.2 });
    };
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      {/* System Health Card */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">System Health</h2>
        <div className="flex items-center gap-4">
          <div className={`text-4xl font-bold ${systemHealth.status === 'healthy' ? 'text-emerald-400' : 'text-yellow-500'}`}>
            {systemHealth.score}%
          </div>
          <div className="text-gray-400">Overall system status: <span className="capitalize">{systemHealth.status}</span></div>
        </div>
        <div className="mt-4 w-full bg-gray-700 rounded-full h-2">
          <div className="bg-emerald-400 h-2 rounded-full" style={{ width: `${systemHealth.score}%` }}></div>
        </div>
      </div>

      {/* Cloud Function Status Table */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Cloud Functions Status</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left">Function Name</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Last Invocation</th>
              </tr>
            </thead>
            <tbody>
              {functionStatuses.map(fn => (
                <tr key={fn.name} className="border-t border-gray-700">
                  <td className="px-4 py-2 font-mono">{fn.name}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${fn.status === 'active' ? 'bg-emerald-900 text-emerald-300' : 'bg-yellow-900 text-yellow-300'}`}>
                      {fn.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">{timeAgo(fn.lastInvocation)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Queue Depths */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Queue Depths</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{queueDepths.dispatch || 0}</div>
            <div className="text-gray-400 text-sm">Dispatch Queue</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{queueDepths.payment || 0}</div>
            <div className="text-gray-400 text-sm">Payment Queue</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{queueDepths.notification || 0}</div>
            <div className="text-gray-400 text-sm">Notification Queue</div>
          </div>
        </div>
      </div>

      {/* Real-time Metrics */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Real‑time Metrics</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{metrics.ridesLastHour || 0}</div>
            <div className="text-gray-400 text-sm">Rides (last hour)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{metrics.apiLatency || 0} ms</div>
            <div className="text-gray-400 text-sm">Avg API Latency</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{metrics.errorRate || 0}%</div>
            <div className="text-gray-400 text-sm">Error Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};
